import React, { useEffect, useState } from 'react'

const TIMEOUT_MINUTES = 3
const LOCALSTORAGE_PREFIX = 'callback_request_timestamp_'

export function CallbackRequestTimer({
  showCallButton,
  accent,
  toolCallId,
  isLiveChat
}: {
  isLiveChat: boolean
  showCallButton: boolean
  accent: string
  toolCallId: string
}) {
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  useEffect(() => {
    // Get the timestamp when this callback request was first shown
    const storageKey = `${LOCALSTORAGE_PREFIX}${toolCallId}`
    const storedTimestamp = localStorage.getItem(storageKey)
    const now = Date.now()

    if (!storedTimestamp) {
      // First time seeing this callback request, store the timestamp
      localStorage.setItem(storageKey, now.toString())
      setHasTimedOut(false)
      setTimeRemaining(TIMEOUT_MINUTES * 60) // Start with full time in seconds
    } else {
      // Calculate if timeout has occurred
      const timestamp = parseInt(storedTimestamp, 10)
      const elapsedMs = now - timestamp
      const elapsedMinutes = elapsedMs / (1000 * 60)

      if (elapsedMinutes >= TIMEOUT_MINUTES) {
        setHasTimedOut(true)
        setTimeRemaining(0)
      } else {
        // Still within timeout period
        setHasTimedOut(false)
        const remainingSeconds = Math.floor(
          TIMEOUT_MINUTES * 60 - elapsedMs / 1000
        )
        setTimeRemaining(remainingSeconds)

        // Set up a timer to update the remaining time
        const timer = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(timer)
              setHasTimedOut(true)
              return 0
            }
            return prev - 1
          })
        }, 1000)

        return () => clearInterval(timer)
      }
    }
  }, [toolCallId])

  // Format the remaining time as mm:ss
  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  if (isLiveChat) {
    return null
  }

  if (hasTimedOut) {
    return (
      <div className="rounded-lg bg-gray-100 p-4 text-sm">
        {timeRemaining !== null && (
          <span className="mb-1.5 text-xs font-semibold text-[#5C6370]">
            Est. wait time: {formatTimeRemaining(timeRemaining)}
          </span>
        )}
        <p className="text-sm font-medium text-gray-800">
          No one was available to join live, but your info&apos;s with the Titan
          team and they&apos;ll reach out within 24 hours. For faster help, you
          can also call them at:
        </p>

        {showCallButton && (
          <a
            href="tel:8885446531"
            className="mt-2 flex items-center justify-center gap-1 rounded-lg p-2 text-sm text-white shadow-md"
            style={{ backgroundColor: accent }}
          >
            <span className="font-bold">Call Us:</span>
            (888) 544-6531
          </a>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-blue-50 p-4 text-sm">
      {timeRemaining !== null && (
        <span className="mb-1.5 text-xs font-semibold text-[#5C6370]">
          Est. wait time: {formatTimeRemaining(timeRemaining)}
        </span>
      )}
      <p className="text-sm font-semibold text-blue-800">
        Thanks—we&apos;ve sent your info to the Titan team. Checking who&apos;s
        available to connect now.
      </p>
      {showCallButton && (
        <a
          href="tel:8885446531"
          className="mt-2 flex items-center justify-center gap-1 rounded-lg p-2 text-sm text-white shadow-md"
          style={{ backgroundColor: accent }}
        >
          <span className="font-bold">Call Us Now:</span>
          (888) 544-6531
        </a>
      )}
    </div>
  )
}
